import type { Area } from 'react-easy-crop'

export type CropExportOptions = {
  /** MIME for the exported file. Prefer image/png for logos with transparency. */
  mimeType?: string
  /** JPEG/WebP quality 0–1. */
  quality?: number
  /** Longest edge cap in pixels (width or height). */
  maxEdge?: number
  fileName?: string
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.addEventListener('load', () => resolve(img))
    img.addEventListener('error', () => reject(new Error('Could not load image for cropping')))
    img.crossOrigin = 'anonymous'
    img.src = src
  })
}

/**
 * Crop `imageSrc` to `pixelCrop` and return a File suitable for upload endpoints.
 */
export async function getCroppedImageFile(
  imageSrc: string,
  pixelCrop: Area,
  options: CropExportOptions = {},
): Promise<File> {
  const {
    mimeType = 'image/jpeg',
    quality = 0.92,
    maxEdge = 1920,
    fileName = 'cropped-image',
  } = options

  const image = await loadImage(imageSrc)
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas is not available')

  const { width, height } = pixelCrop
  if (width <= 0 || height <= 0) {
    throw new Error('Invalid crop area')
  }

  const longest = Math.max(width, height)
  const scale = longest > maxEdge ? maxEdge / longest : 1
  const outW = Math.max(1, Math.round(width * scale))
  const outH = Math.max(1, Math.round(height * scale))

  canvas.width = outW
  canvas.height = outH

  if (mimeType === 'image/jpeg') {
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, outW, outH)
  }

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    outW,
    outH,
  )

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => {
        if (!b) reject(new Error('Failed to export cropped image'))
        else resolve(b)
      },
      mimeType,
      quality,
    )
  })

  const ext =
    mimeType === 'image/png' ? 'png' : mimeType === 'image/webp' ? 'webp' : 'jpg'
  const base = fileName.replace(/\.[^.]+$/, '') || 'cropped-image'
  return new File([blob], `${base}.${ext}`, { type: mimeType })
}

/** Prefer PNG when the source can carry transparency (logos). */
export function exportMimeForSource(sourceMime: string, preferPng: boolean): string {
  if (preferPng && (sourceMime === 'image/png' || sourceMime === 'image/webp' || sourceMime === 'image/gif')) {
    return 'image/png'
  }
  if (sourceMime === 'image/webp') return 'image/webp'
  return 'image/jpeg'
}
